import { Flex } from '@mantine/core';
import { type IObservableArray } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import type { DisplayCreative, Goal, Partner, Targeting, TTDCampaign } from '@feathr/blackbox';
import { CardV2 as Card } from '@feathr/components';
import type { ListResponse } from '@feathr/rachis';
import { TargetAudienceOverview, useAudienceBreakdown, useFlags } from '@feathr/report_components';

import type { ICampaignValidationErrors } from '../../../CampaignSummary';
import CampaignSummary from '../../../CampaignSummary';
import TTDRecommendedOptimizationModal from '../TTDRecommendedOptimizationModal';

import * as styles from '../AdWizard.css';

export interface IAdWizardReviewStepProps {
  campaign: TTDCampaign;
  targetings: ListResponse<Targeting>;
  goals: IObservableArray<Goal>;
  creatives: IObservableArray<DisplayCreative>;
  getValidationErrors: () => ICampaignValidationErrors;
}

export function validatePartner(partner: Partner): string[] {
  return partner.validate([], false).errors;
}

function AdWizardReviewStep({
  campaign,
  creatives,
  targetings,
  getValidationErrors,
}: Readonly<IAdWizardReviewStepProps>): JSX.Element {
  const { showLookalikeAudienceTargeting } = useFlags();
  const location = useLocation();
  const audienceData = useAudienceBreakdown({
    targetings,
    campaign,
  });

  const queryParams = new URLSearchParams(location.search);
  const additionalBudgetParam = Number(queryParams.get('additionalBudget'));

  const { isOmniCampaign, isPastEndDate, isMonetization, isPublished } = campaign;
  const { t } = useTranslation();

  // Never prompt the user to increase budget if the campaign has reached its end date
  const showRecommendationsModal =
    !!additionalBudgetParam && !isPastEndDate && !isMonetization && isPublished;

  const errors = getValidationErrors();

  return (
    <Flex
      className={isOmniCampaign ? styles.flexRoot : styles.formRoot}
      data-name={'review_step'}
      justify={isOmniCampaign ? 'center' : 'space-between'}
    >
      {showRecommendationsModal && (
        <TTDRecommendedOptimizationModal campaign={campaign} validate={getValidationErrors} />
      )}
      <Card width={'wide'}>
        <Card.Header
          description={t('Review the summary below to make sure everything is set up correctly.')}
          title={t('Review')}
        />
        <Card.Content>
          <CampaignSummary
            campaign={campaign}
            creatives={creatives}
            layout={'vertical'}
            validationErrors={errors}
          />
        </Card.Content>
      </Card>
      {isOmniCampaign && (
        <div className={styles.right}>
          <TargetAudienceOverview
            showLookalikeAudienceTargeting={showLookalikeAudienceTargeting}
            {...audienceData}
          />
        </div>
      )}
    </Flex>
  );
}

export default observer(AdWizardReviewStep);
